/**
 * Centralized SEO configuration for all pages.
 * All page titles, meta descriptions, H1 text, robots directives, and canonical URLs.
 *
 * Copy sourced from the SE Ranking audit / SEO brief (March 2026).
 */

const BASE_URL = 'https://game.city';

export const SEO_DEFAULTS = {
    siteName: 'Game.city',
    ogImage: `${BASE_URL}/logo-og.png`,
    twitterCard: 'summary_large_image',
    robots: 'index, follow',
};

/**
 * SEO metadata keyed by route path.
 */
export const SEO_CONFIG = {
    '/': {
        title: 'TV Party Games for Your Living Room | Game.city',
        description: 'Play brilliant TV party games in your living room. Use your phone as a controller. Featuring Catchphrase, Pointless, The 1% Club and more. No console needed.',
        h1: 'TV Party Games for Your Living Room',
        robots: 'index, follow',
        canonical: BASE_URL + '/',
    },
    '/shop': {
        title: 'Buy TV Party Games | Play on Your TV Tonight | Game.city',
        description: 'Browse Game.city\'s full library of TV party games. One-time purchase, play instantly on your Smart TV. Phone as controller \u2014 no console required.',
        h1: 'Buy TV Party Games \u2014 Play Tonight on Your Big Screen',
        robots: 'index, follow',
        canonical: BASE_URL + '/shop',
    },
    '/help': {
        title: 'Help & Support | Game.city',
        description: 'Need help with Game.city? Find answers to common questions about setup, TV connection, and gameplay.',
        h1: 'Help & Support',
        robots: 'index, follow',
        canonical: BASE_URL + '/help',
    },
    '/login': {
        title: 'Log In to Your Game.city Account',
        description: 'Log in to your Game.city account to access your purchased TV party games.',
        h1: 'Log In to Game.city',
        robots: 'noindex, follow',
        canonical: BASE_URL + '/login',
    },
    '/join': {
        title: 'Join a Game | Game.city TV Party Games',
        description: 'Joining a Game.city game session? Enter your room code here and use your phone as a controller.',
        h1: 'Join a Game.city Session',
        robots: 'noindex, follow',
        canonical: BASE_URL + '/join',
    },
    '/checkout': {
        title: 'Checkout | Game.city',
        description: 'Complete your Game.city purchase and start playing on your TV tonight.',
        h1: 'Complete Your Order',
        robots: 'noindex, follow',
        canonical: BASE_URL + '/checkout',
    },
    '/cookie-policy': {
        title: 'Cookie Policy | Game.city',
        description: 'Learn how Game.city uses cookies on our website. Read our full cookie policy here.',
        h1: 'Cookie Policy',
        robots: 'index, follow',
        canonical: BASE_URL + '/cookie-policy',
    },
    '/privacy-policy': {
        title: 'Privacy Policy | Game.city',
        description: 'Read Game.city\'s privacy policy. We explain how we collect, use and protect your personal data.',
        h1: 'Privacy Policy',
        robots: 'index, follow',
        canonical: BASE_URL + '/privacy-policy',
    },
    '/data-protection': {
        title: 'Data Protection Policy | Game.city',
        description: 'Game.city\'s data protection policy \u2014 how we handle and safeguard your information in line with UK GDPR.',
        h1: 'Data Protection Policy',
        robots: 'index, follow',
        canonical: BASE_URL + '/data-protection',
    },
    '/terms': {
        title: 'Terms and Conditions | Game.city',
        description: 'Read Game.city\'s terms and conditions for purchasing and using our TV party games platform.',
        h1: 'Terms and Conditions',
        robots: 'index, follow',
        canonical: BASE_URL + '/terms',
    },
    '/redeem-code': {
        title: 'Redeem Code | Game.city',
        description: 'Redeem your Game.city activation code to unlock party games on your TV.',
        h1: 'Redeem Code',
        robots: 'noindex, follow',
        canonical: BASE_URL + '/redeem-code',
    },
    '/my-account': {
        title: 'My Account | Game.city',
        description: 'Manage your Game.city account, view your games, and update your settings.',
        h1: 'My Account',
        robots: 'noindex, follow',
        canonical: BASE_URL + '/my-account',
    },
};

/**
 * SEO overrides for individual game product pages (/shop/:slug).
 * Keyed by the slug portion of the URL (e.g., "catchphrase" for /shop/catchphrase).
 * Falls back to dynamic game data if slug is not listed here.
 *
 * Copy from SEO brief PDF (March 2026).
 */
export const GAME_SEO_OVERRIDES = {
    'catchphrase': {
        title: 'Play Catchphrase on Your TV at Home | Game.city',
        description: 'Play the official Catchphrase game on your TV at home. Everyone uses their phone as a buzzer. Perfect for family game nights.',
        h1: 'Play Catchphrase on Your TV at Home',
    },
    'deal-or-no-deal': {
        title: 'Play Deal or No Deal on Your TV | Game.city',
        description: 'Play Deal or No Deal on your TV with friends and family. Will you take the banker\'s offer? Find out on game night.',
        h1: 'Play Deal or No Deal on Your TV',
    },
    'herd-mentality': {
        title: 'Play Herd Mentality on Your TV | Game.city',
        description: 'Think like the herd in this hilarious TV party game. Answer questions the same way as everyone else to win. Play on your TV tonight.',
        h1: 'Play Herd Mentality on Your TV',
    },
    'universally-challenged': {
        title: 'Play Universally Challenged on Your TV | Game.city',
        description: 'Test your general knowledge with Universally Challenged on your TV. Perfect for quiz nights at home with friends and family.',
        h1: 'Play Universally Challenged on Your TV',
    },
    'poker': {
        title: 'Play Poker Night on Your TV | Game.city',
        description: 'Bring the poker table to your living room TV. A fun party version of poker using your phones as controllers.',
        h1: 'Play Poker Night on Your TV',
    },
    'scrawl': {
        title: 'Play Scrawl Drawing Game on Your TV | Game.city',
        description: 'The hilariously bad drawing game, now on your TV. Draw prompts on your phone and watch the chaos unfold on the big screen.',
        h1: 'Play Scrawl on Your TV \u2014 The Drawing Game',
    },
    'brain-games': {
        title: 'Play Brain Games on Your TV | Game.city',
        description: 'Put your brain to the test with Brain Games on your TV. Fun mental challenges for all the family using phones as controllers.',
        h1: 'Play Brain Games on Your TV',
    },
    'game-pack-1': {
        title: 'Game Pack 1 \u2013 TV Party Game Bundle | Game.city',
        description: 'Get three brilliant TV party games in one bundle. Game Pack 1 is the perfect starter set for game nights at home.',
        h1: 'Game Pack 1 \u2014 Three TV Party Games in One Bundle',
    },
    'game-pack-2': {
        title: 'Game Pack 2 \u2013 TV Party Game Bundle | Game.city',
        description: 'More TV party game action in one great bundle. Game Pack 2 adds variety to your game night lineup.',
        h1: 'Game Pack 2 \u2014 More TV Party Games at Home',
    },
    'playing-with-fire': {
        title: 'Play Playing With Fire on Your TV | Game.city',
        description: 'A spicy party game for daring groups. Playing With Fire brings bold questions and big laughs to your TV game night.',
        h1: 'Play Playing With Fire on Your TV',
    },
    'pub-quiz': {
        title: 'Play Pub Quiz on Your TV at Home | Game.city',
        description: 'Bring pub quiz night home. Play on your TV with teams, live scoring and multiple rounds. Phones as buzzers \u2014 no pub required.',
        h1: 'Play Pub Quiz on Your TV at Home',
    },
    'you-cant-say-umm': {
        title: 'Play You Can\'t Say Umm on Your TV | Game.city',
        description: 'The game where hesitation loses you the round. Play You Can\'t Say Umm on your TV for fast, frantic family fun.',
        h1: 'Play You Can\'t Say Umm on Your TV',
    },
    'the-chameleon': {
        title: 'Play The Chameleon on Your TV | Game.city',
        description: 'One player is the Chameleon \u2014 can the group expose them? A brilliant social deduction game for your TV.',
        h1: 'Play The Chameleon on Your TV',
    },
    'colour-brain': {
        title: 'Play Colour Brain on Your TV | Game.city',
        description: 'Only some players can see the right answer in Colour Brain. Outsmart your friends in this unique TV party game.',
        h1: 'Play Colour Brain on Your TV',
    },
    'draw-along-with-dave': {
        title: 'Play Draw Along With Dave on Your TV | Game.city',
        description: 'A drawing game with a twist. Draw Along With Dave brings creative chaos to your TV screen.',
        h1: 'Play Draw Along With Dave on Your TV',
    },
    'anagram-magic': {
        title: 'Play Anagram Magic on Your TV | Game.city',
        description: 'Unscramble words faster than your friends in Anagram Magic. A great word game for your TV game night.',
        h1: 'Play Anagram Magic on Your TV',
    },
    'maths-master': {
        title: 'Play Maths Master on Your TV | Game.city',
        description: 'Who\'s the quickest with numbers? Maths Master brings fast-paced number challenges to your TV party night.',
        h1: 'Play Maths Master on Your TV',
    },
    'word-wheel': {
        title: 'Play Word Wheel on Your TV | Game.city',
        description: 'Race to find words in the wheel before your friends do. Word Wheel is a fast, fun TV game for all ages.',
        h1: 'Play Word Wheel on Your TV',
    },
    'can-of-squirms': {
        title: 'Play Can of Squirms on Your TV | Game.city',
        description: 'Awkward questions, unexpected answers. Can of Squirms is the party game that reveals what your friends really think.',
        h1: 'Play Can of Squirms on Your TV',
    },
};

/**
 * Helper: get SEO config for a given path.
 * Returns the config object or null if not found.
 */
export const getSeoConfig = (path) => {
    return SEO_CONFIG[path] || null;
};

/**
 * Helper: get SEO overrides for a game slug.
 * Strips /shop/ prefix if present.
 */
export const getGameSeoOverrides = (slug) => {
    const normalizedSlug = slug?.replace(/^\/shop\//, '') || '';
    return GAME_SEO_OVERRIDES[normalizedSlug] || null;
};
